import React, { useState } from 'react';
import { Card, Typography, Alert, Spin, Button, Collapse, List, Empty, Tag } from 'antd';
import { ProfessionalType } from '../../../types';
import MaterialIcon from '../../Icons/MaterialIcon';
import { useAIAnalysis } from '../../Questionnaire/NewAI/shared/hooks';
import { AIAnalysisResult } from '../../Questionnaire/NewAI/shared/types';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

interface AIAnalysisResultProps {
  // The professional user, for language preference
  user?: ProfessionalType;
  // The client whose Analysis Results are being viewed
  client: any;
}

const AIAnalysisResultComponent: React.FC<AIAnalysisResultProps> = ({ user, client }) => {
  const [showResults, setShowResults] = useState(false);
  
  // Use the shared hook to fetch AI analysis
  const { aiAnalysis, loading, error } = useAIAnalysis(client);

  // Log component render and props
  console.log('[AIAnalysisResult] Component rendered with props:');
  console.log('- user:', user);
  console.log('- client:', client);
  console.log('- client.id:', client?.id);
  console.log('- aiAnalysis:', aiAnalysis);

  // Determine the language from the professional's profile, default to 'en'
  const _userAny = user as any;
  const detectedLanguage = _userAny?.userAttributes?.language ?? _userAny?.language ?? 'en';
  const lang = detectedLanguage === 'nl' ? 'nl' : 'en';

  const loadingText = {
    en: 'Loading analysis results...',
    nl: 'Analyseresultaten laden...',
  };

  const noResultsText = {
    en: {
      title: 'No Analysis Results Available',
      message: 'Analysis results for this client have not been generated yet.',
      instructions: 'To generate analysis results, please ensure the client has completed the My Health Intake questionnaire, then submit it for analysis in the Questionnaire Responses section.',
    },
    nl: {
      title: 'Geen Analyseresultaten Beschikbaar',
      message: 'Analyseresultaten voor deze cliënt zijn nog niet gegenereerd.',
      instructions: 'Om analyseresultaten te genereren, zorg ervoor dat de cliënt de My Health Intake vragenlijst heeft voltooid en dien deze vervolgens in voor analyse in het gedeelte Vragenlijst Antwoorden.',
    },
  };

  const titles = {
    en: 'Analysis Result',
    nl: 'Analyse Resultaat',
  };

  const handleViewResults = () => {
    console.log('[AIAnalysisResult] handleViewResults called - toggling visibility');
    setShowResults(!showResults);
  };

  // Helper function to format endoteleon names
  const formatEndoteleonName = (name: string): string => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to format biochemical marker names
  const formatBiochemicalName = (name: string): string => {
    return name.toUpperCase();
  };

  const renderAnalysisResult = (result: AIAnalysisResult) => (
    <Panel
      key="analysis-result"
      header={
        <div>
          <strong>{lang === 'en' ? 'Analysis' : 'Analyse'}</strong>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            {lang === 'en' ? 'Client' : 'Cliënt'}: {result['client-name']}
          </div>
        </div>
      }
    >
      {/* A1 Section - Primary and Secondary Analysis */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'A1: Primary & Secondary Endoteleon Analysis' : 'A1: Primaire & Secundaire Endoteleon Analyse'}
        </Title>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div>
            <Text strong>{lang === 'en' ? 'Primary:' : 'Primair:'}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                {formatEndoteleonName(result.A1.primary_ie)}
              </Tag>
            </div>
            
            <Text strong>{lang === 'en' ? 'Secondary:' : 'Secundair:'}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              {result.A1.secondary_ie.map((item: string, index: number) => (
                <Tag key={index} color="green" style={{ fontSize: '14px', padding: '4px 8px', marginBottom: '4px' }}>
                  {formatEndoteleonName(item)}
                </Tag>
              ))}
            </div>
            
            <Text strong>{lang === 'en' ? 'Teleonic Pattern:' : 'Teleonisch Patroon:'}</Text>
            <Paragraph style={{ marginTop: 8, backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              {result.A1.teleonic_pattern}
            </Paragraph>

            <Text strong>{lang === 'en' ? 'Uncertainty Level:' : 'Onzekerheidsniveau:'}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={result.A1.uncertainty_level > 0.7 ? 'red' : result.A1.uncertainty_level > 0.4 ? 'orange' : 'green'}>
                {result.A1.uncertainty_label}
              </Tag>
            </div>
          </div>
        </Card>
      </div>

      {/* A2 Section - Endoteleon Priority List */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'A2: Endoteleon Priority List' : 'A2: Endoteleon Prioriteitenlijst'}
        </Title>
        <Card size="small">
          <List
            dataSource={result.A2}
            renderItem={(item: string, index: number) => (
              <List.Item>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                  <Text strong style={{ marginRight: 16 }}>{index + 1}.</Text>
                  <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {formatEndoteleonName(item)}
                  </Tag>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* A3 Section - Biochemical Markers */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'A3: Recommended Biochemical Markers' : 'A3: Aanbevolen Biochemische Markers'}
        </Title>
        <Card size="small">
          <div style={{ marginBottom: 16 }}>
            <Text strong>{lang === 'en' ? 'Direct Markers:' : 'Directe Markers:'}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3.direct.map((marker: string, index: number) => (
                <Tag key={index} color="orange" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
          <div>
            <Text strong>{lang === 'en' ? 'Indirect Markers:' : 'Indirecte Markers:'}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3.indirect.map((marker: string, index: number) => (
                <Tag key={index} color="cyan" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* B1 Section - Recommended Questionnaires (Primary) */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'B1: Primary Recommended Questionnaires' : 'B1: Primaire Aanbevolen Vragenlijsten'}
        </Title>
        <Card size="small">
          <List
            dataSource={result.B1 as any[]}
            renderItem={(questionnaire: any) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong style={{ marginRight: 12 }}>{questionnaire.nr}.</Text>
                    <Text strong>{questionnaire.label}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ID: {questionnaire.ID || questionnaire.id || 'N/A'}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* B2 Section - Recommended Questionnaires (Secondary) */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'B2: Secondary Recommended Questionnaires' : 'B2: Secundaire Aanbevolen Vragenlijsten'}
        </Title>
        <Card size="small">
          <List
            dataSource={result.B2 as any[]}
            renderItem={(questionnaire: any) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong style={{ marginRight: 12 }}>{questionnaire.nr}.</Text>
                    <Text strong>{questionnaire.label}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ID: {questionnaire.ID || questionnaire.id || 'N/A'}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>

      {/* B3 Section - HETA Recommendations */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          {lang === 'en' ? 'B3: HETA Recommendations' : 'B3: HETA Aanbevelingen'}
        </Title>
        <Card size="small">
          <List
            dataSource={result.B3 as any[]}
            renderItem={(heta: any) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong style={{ marginRight: 12 }}>{heta.nr}.</Text>
                    <Text strong>{lang === 'en' ? 'HETA:' : 'HETA:'}</Text>
                    <Tag color="gold" style={{ marginLeft: 8 }}>
                      {heta.label}
                    </Tag>
                  </div>
                  {heta.tier && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      {lang === 'en' ? 'Tier:' : 'Tier:'} {heta.tier}
                    </Text>
                  )}
                  {heta.heta_id && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      {lang === 'en' ? 'ID:' : 'ID:'} {heta.heta_id}
                    </Text>
                  )}
                </div>
              </List.Item>
            )}
          />
        </Card>
      </div>
    </Panel>
  );

  const hasResults = !!aiAnalysis;

  return (
    <Card>
      <Title level={5}>{titles[lang]}</Title>
      
      {/* {error && (
        <Alert
          message={lang === 'en' ? 'Error' : 'Fout'}
          description={error}
          type="error"
          style={{ marginBottom: 16 }}
        />
      )} */}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Paragraph>{loadingText[lang]}</Paragraph>
          </div>
        </div>
      ) : hasResults ? (
        <>
          <Paragraph>
            {lang === 'en' 
              ? `Analysis results available for ${aiAnalysis?.['client-name'] || 'this client'}.`
              : `Analyseresultaten beschikbaar voor ${aiAnalysis?.['client-name'] || 'deze cliënt'}.`
            }
          </Paragraph>
          
          <Button 
            type={showResults ? "default" : "primary"}
            onClick={handleViewResults}
            style={{ marginBottom: showResults ? 16 : 0 }}
            icon={<MaterialIcon className="material-icons-sharp" icon={showResults ? "visibility_off" : "visibility"} />}
          >
            {showResults 
              ? (lang === 'en' ? 'Hide Results' : 'Verberg Resultaten')
              : (lang === 'en' ? 'Show Results' : 'Toon Resultaten')
            }
          </Button>

          {showResults && aiAnalysis && (
            <Collapse accordion defaultActiveKey={['analysis-result']}>
              {renderAnalysisResult(aiAnalysis)}
            </Collapse>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <Title level={5} style={{ color: '#666', marginBottom: 16 }}>
                  {noResultsText[lang].title}
                </Title>
                <Paragraph style={{ color: '#888', marginBottom: 16 }}>
                  {noResultsText[lang].message}
                </Paragraph>
                {/* <Alert
                  message={lang === 'en' ? 'How to Generate Results' : 'Hoe Resultaten Te Genereren'}
                  description={noResultsText[lang].instructions}
                  type="info"
                  showIcon
                  icon={<MaterialIcon className="material-icons-sharp" icon="info" />}
                  style={{ textAlign: 'left' }}
                /> */}
              </div>
            }
          />
        </div>
      )}
    </Card>
  );
};

export default AIAnalysisResultComponent;
